import React, { useState } from 'react';
import { useCart } from '../../context/CartContext';

/* ===== CHECKOUT MODAL ===== */
export function CheckoutModal({ onClose, onSuccess }) {
  const { items, total, shipping, grandTotal, clearCart } = useCart();
  const [form, setForm] = useState({ name: '', phone: '', email: '', address: '', city: '', state: '', pincode: '' });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(false);

  function set(e) { setForm(f => ({ ...f, [e.target.name]: e.target.value })); }

  function validate(f) {
    const e = {};
    if (!f.name.trim()) e.name = 'Full name is required.';
    else if (f.name.trim().length < 2) e.name = 'Name must be at least 2 characters.';
    else if (!/^[a-zA-Z\s'\-.]+$/.test(f.name.trim())) e.name = 'Name should contain only letters.';

    if (!f.phone.trim()) e.phone = 'Phone number is required.';
    else if (!/^\d{10}$/.test(f.phone.trim())) e.phone = 'Enter a valid 10-digit mobile number.';

    if (f.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim()))
      e.email = 'Enter a valid email address.';

    if (!f.address.trim()) e.address = 'Delivery address is required.';
    else if (f.address.trim().length < 5) e.address = 'Please enter your complete address.';

    if (!f.city.trim()) e.city = 'City is required.';

    if (!f.pincode.trim()) e.pincode = 'Pincode is required.';
    else if (!/^\d{6}$/.test(f.pincode.trim())) e.pincode = 'Enter a valid 6-digit pincode.';

    return e;
  }

  function handleBlur(e) {
    const { name } = e.target;
    setTouched(t => ({ ...t, [name]: true }));
    setErrors(validate(form));
  }

  function loadScript(src) {
    return new Promise(res => {
      if (window.Cashfree) { res(true); return; }
      // Remove any previously failed script tag before retrying
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) existing.remove();
      const s = document.createElement('script');
      s.src = src;
      s.onload = () => res(true);
      s.onerror = () => res(false);
      document.body.appendChild(s);
    });
  }

  // Safe localStorage helpers — localStorage can throw in iOS Safari private browsing
  function savePendingOrder(data) {
    try { localStorage.setItem('kiane_pending_order', JSON.stringify({ ...data, savedAt: Date.now() })); } catch {/* ignore */}
  }
  function clearPendingOrder() {
    try { localStorage.removeItem('kiane_pending_order'); } catch {/* ignore */}
  }

  async function handlePay(e) {
    e.preventDefault();
    setSubmitError('');
    setTouched({ name: true, phone: true, email: true, address: true, city: true, state: true, pincode: true });
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setLoading(true);
    try {
      const ok = await loadScript('https://sdk.cashfree.com/js/v3/cashfree.js');
      if (!ok) {
        setSubmitError('Could not load payment gateway. Please check your internet connection and try again.');
        setLoading(false);
        return;
      }
      const res = await fetch('/api/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer: form,
          items: items.map(i => ({ id: i.id, name: i.name, price: i.price, qty: i.qty, image: i.image })),
          subtotal: total,
          shipping,
          total: grandTotal,
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to create order. Please try again.');
      }
      const order = await res.json();

      // Persist before opening the payment modal so we can recover if the page
      // reloads mid-payment (common on mobile when the UPI app is launched)
      savePendingOrder({ orderId: order.orderId, cfOrderId: order.cfOrderId });

      const cashfree = window.Cashfree({ mode: order.cfMode });
      const result = await cashfree.checkout({
        paymentSessionId: order.paymentSessionId,
        redirectTarget: '_modal',
      });

      // User explicitly closed the modal — no payment was attempted
      if (result.error?.type === 'USER_DROP') {
        clearPendingOrder();
        setSubmitError('Payment cancelled. You can try again.');
        setLoading(false);
        return;
      }

      // For every other outcome — success, SDK error, or ambiguous —
      // always verify with the backend. It calls Cashfree directly and is
      // the single authoritative source of truth for payment status.
      try {
        const vRes = await fetch('/api/verify-payment', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderId: order.orderId, cfOrderId: order.cfOrderId }),
        });
        if (vRes.ok) {
          clearPendingOrder();
          clearCart();
          onSuccess(order.orderId);
        } else {
          // Backend confirmed payment did not go through
          setSubmitError(
            result.error
              ? (result.error.message || 'Payment failed. Please try again.')
              : `Payment received but confirmation is pending. Your Order ID: ${order.orderId} — save this and use Track Order or contact us.`
          );
          setLoading(false);
        }
      } catch {
        setSubmitError(`Payment status unknown. Your Order ID: ${order.orderId} — use Track Order to check or contact us if payment was deducted.`);
        setLoading(false);
      }
    } catch (err) {
      console.error(err);
      setSubmitError(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  }

  const fieldErr = (field) => touched[field] && errors[field]
    ? <span className="field-error">{errors[field]}</span>
    : null;
  const inputCls = (field) => touched[field] && errors[field] ? 'input-error' : '';

  return (
    <div className="modal-overlay" onClick={e => { if (!loading && e.target === e.currentTarget) onClose(); }}>
      <div className="modal-box checkout-modal">
        <div className="modal-header">
          <h2>Checkout</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="checkout-body">
          <div className="checkout-summary">
            <h4>Order Summary</h4>
            {items.map(i => (
              <div className="checkout-row" key={i.id}>
                <span>{i.name} × {i.qty}</span>
                <span>₹{i.price * i.qty}</span>
              </div>
            ))}
            <div className="checkout-row">
              <span>Shipping</span>
              {shipping === 0
                ? <span><s>₹75</s> FREE</span>
                : <span>₹75</span>
              }
            </div>
            <div className="checkout-row checkout-total-row">
              <strong>Total</strong>
              <strong>₹{grandTotal}</strong>
            </div>
          </div>
          <form className="checkout-form" onSubmit={handlePay} noValidate>
            <h4>Delivery Details</h4>
            <div className="form-row-2">
              <label className="form-label">
                Full Name *
                <input name="name" value={form.name} onChange={set} onBlur={handleBlur} placeholder="Riya Sharma" autoComplete="name" className={inputCls('name')} />
                {fieldErr('name')}
              </label>
              <label className="form-label">
                <span>Phone * <small>(10 digits)</small></span>
                <input name="phone" type="tel" inputMode="numeric" value={form.phone} onChange={set} onBlur={handleBlur} placeholder="9876543210" maxLength={10} autoComplete="tel-national" className={inputCls('phone')} />
                {fieldErr('phone')}
              </label>
            </div>
            <label className="form-label">
              <span>Email <small>(optional)</small></span>
              <input name="email" type="email" inputMode="email" value={form.email} onChange={set} onBlur={handleBlur} placeholder="riya@example.com" autoComplete="email" className={inputCls('email')} />
              {fieldErr('email')}
            </label>
            <label className="form-label">
              Delivery Address *
              <input name="address" value={form.address} onChange={set} onBlur={handleBlur} placeholder="House no., Street, Area" autoComplete="street-address" className={inputCls('address')} />
              {fieldErr('address')}
            </label>
            <div className="form-row-2">
              <label className="form-label">
                City *
                <input name="city" value={form.city} onChange={set} onBlur={handleBlur} placeholder="Chandigarh" autoComplete="address-level2" className={inputCls('city')} />
                {fieldErr('city')}
              </label>
              <label className="form-label">
                State
                <select name="state" value={form.state} onChange={set} className="form-select">
                  <option value="">Select state / UT</option>
                  <optgroup label="States">
                    <option>Andhra Pradesh</option>
                    <option>Arunachal Pradesh</option>
                    <option>Assam</option>
                    <option>Bihar</option>
                    <option>Chhattisgarh</option>
                    <option>Goa</option>
                    <option>Gujarat</option>
                    <option>Haryana</option>
                    <option>Himachal Pradesh</option>
                    <option>Jharkhand</option>
                    <option>Karnataka</option>
                    <option>Kerala</option>
                    <option>Madhya Pradesh</option>
                    <option>Maharashtra</option>
                    <option>Manipur</option>
                    <option>Meghalaya</option>
                    <option>Mizoram</option>
                    <option>Nagaland</option>
                    <option>Odisha</option>
                    <option>Punjab</option>
                    <option>Rajasthan</option>
                    <option>Sikkim</option>
                    <option>Tamil Nadu</option>
                    <option>Telangana</option>
                    <option>Tripura</option>
                    <option>Uttar Pradesh</option>
                    <option>Uttarakhand</option>
                    <option>West Bengal</option>
                  </optgroup>
                  <optgroup label="Union Territories">
                    <option>Andaman and Nicobar Islands</option>
                    <option>Chandigarh</option>
                    <option>Dadra and Nagar Haveli and Daman and Diu</option>
                    <option>Delhi (NCT)</option>
                    <option>Jammu and Kashmir</option>
                    <option>Ladakh</option>
                    <option>Lakshadweep</option>
                    <option>Puducherry</option>
                  </optgroup>
                </select>
              </label>
            </div>
            <label className="form-label">
              Pincode *
              <input name="pincode" inputMode="numeric" value={form.pincode} onChange={set} onBlur={handleBlur} placeholder="160001" maxLength={6} autoComplete="postal-code" className={inputCls('pincode')} />
              {fieldErr('pincode')}
            </label>
            {submitError && <p className="form-error">{submitError}</p>}
            <button type="submit" className="btn-primary btn-cart checkout-pay-btn" disabled={loading}>
              {loading ? 'Opening Payment…' : `Pay ₹${grandTotal} with Cashfree`}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
